import React, { useState, useEffect } from 'react';
import { loadUsers, createUser, updateUser, deleteUser, changePassword, resetPassword } from '../utils/storage';
import { getRoleName, getRoleDescription } from '../utils/rolePermissions';

const AdminPanel = ({ onBack }) => {
  const [currentUsers, setCurrentUsers] = useState([]);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserFirstName, setNewUserFirstName] = useState('');
  const [newUserLastName, setNewUserLastName] = useState('');
  const [newUserRole, setNewUserRole] = useState('standard');
  const [newUserCells, setNewUserCells] = useState([]);
  const [newUserPlatformSpecialty, setNewUserPlatformSpecialty] = useState('both');
  const [error, setError] = useState('');

  const [editingUser, setEditingUser] = useState(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editRole, setEditRole] = useState('');
  const [editCells, setEditCells] = useState([]);
  const [editPlatformSpecialty, setEditPlatformSpecialty] = useState('both');
  const [editError, setEditError] = useState('');

  // Estados para cambio de contraseña
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const allCells = [
    'C1GA', 'C2GA', 'C3GA', 'C4GA', 'C5GA', 'C6GA', 'C7GA',
    'C1MA', 'C2MA', 'C3MA', 'C4MA',
    'G1M1', 'G1M2', 'G1M3', 'G1M4',
    'G2M1', 'G2M2', 'G2M3', 'G2M4',
    'G3M1', 'G3M2', 'G3M3', 'G3M4',
    'G4M1', 'G4M2', 'G4M3', 'G4M4',
    'G5M1', 'G5M2', 'G5M3', 'G5M4',
    'G6M1', 'G6M2', 'G6M3', 'G6M4',
    'G7M1', 'G7M2', 'G7M3', 'G7M4'
  ];

  useEffect(() => {
    const loadInitialUsers = async () => {
      try {
        const users = await loadUsers();
        setCurrentUsers(users);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    
    loadInitialUsers();
  }, []);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');

    if (!newUserEmail || !newUserPassword || !newUserFirstName || !newUserLastName) {
      setError('Todos los campos son obligatorios (Email, Contraseña, Nombre, Apellido).');
      return;
    }
    if (currentUsers.some(user => user.email === newUserEmail)) {
      setError('Ya existe un usuario con este email.');
      return;
    }

    try {
      await createUser({
        email: newUserEmail,
        password: newUserPassword,
        firstName: newUserFirstName,
        lastName: newUserLastName,
        role: newUserRole,
        cells: newUserRole === 'admin' ? [] : newUserCells,
        platform_specialty: newUserPlatformSpecialty
      });
      
      // Recargar usuarios desde la API
      const users = await loadUsers();
      setCurrentUsers(users);
      
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserFirstName('');
      setNewUserLastName('');
      setNewUserRole('standard');
      setNewUserCells([]);
      setNewUserPlatformSpecialty('both');
      alert('Usuario agregado con éxito.');
    } catch (error) {
      setError('Error al crear usuario: ' + error.message);
    }
  };

  const handleDeleteUser = async (emailToDelete) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar al usuario ${emailToDelete}?`)) {
      try {
        await deleteUser(emailToDelete);
        // Recargar usuarios desde la API
        const users = await loadUsers();
        setCurrentUsers(users);
        alert('Usuario eliminado con éxito.');
      } catch (error) {
        alert('Error al eliminar usuario: ' + error.message);
      }
    }
  };

  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditFirstName(user.firstName);
    setEditLastName(user.lastName);
    setEditRole(user.role);
    setEditCells(user.cells || []);
    setEditPlatformSpecialty(user.platform_specialty || 'both');
    setEditError('');
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setEditError('');

    if (!editFirstName || !editLastName) {
      setEditError('Nombre y Apellido son obligatorios.');
      return;
    }

    try {
      await updateUser(editingUser.email, {
        firstName: editFirstName,
        lastName: editLastName,
        role: editRole,
        cells: editRole === 'admin' ? [] : editCells,
        platform_specialty: editPlatformSpecialty
      });
      
      // Recargar usuarios desde la API
      const users = await loadUsers();
      setCurrentUsers(users);
      setEditingUser(null);
      alert('Usuario actualizado con éxito.');
    } catch (error) {
      setEditError('Error al actualizar usuario: ' + error.message);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (!newPassword || !confirmPassword) {
      setPasswordError('Ambos campos de contraseña son obligatorios.');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden.');
      return;
    }

    try {
      // Obtener el email del administrador actual desde localStorage
      console.log('🔍 Verificando localStorage...');
      
      // Intentar primero con 'currentUser', luego con 'user'
      let userFromStorage = localStorage.getItem('currentUser');
      console.log('📦 Raw data from localStorage (currentUser):', userFromStorage);
      
      if (!userFromStorage) {
        userFromStorage = localStorage.getItem('user');
        console.log('📦 Raw data from localStorage (user):', userFromStorage);
      }
      
      const currentUser = JSON.parse(userFromStorage || '{}');
      console.log('👤 Parsed user object:', currentUser);
      console.log('📧 Admin email:', currentUser.email);
      console.log('🔑 User role:', currentUser.role);
      
      const adminEmail = currentUser.email;
      
      if (!adminEmail) {
        console.error('❌ No se encontró email del administrador');
        console.error('❌ currentUser object:', currentUser);
        console.error('❌ localStorage keys:', Object.keys(localStorage));
        setPasswordError('No se pudo identificar al administrador actual.');
        return;
      }

      console.log('✅ Usando resetPassword con adminEmail:', adminEmail);
      
      // Usar resetPassword en lugar de changePassword para administradores
      await resetPassword(passwordUser.email, newPassword, adminEmail);
      
      setShowPasswordModal(false);
      setPasswordUser(null);
      setNewPassword('');
      setConfirmPassword('');
      alert('Contraseña cambiada con éxito.');
    } catch (error) {
      console.error('❌ Error en handleChangePassword:', error);
      setPasswordError('Error al cambiar la contraseña: ' + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header Premium con gradiente */}
        <div className="bg-gradient-to-r from-[#0061A8] to-[#004d8a] rounded-2xl shadow-xl p-6 sm:p-8 mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div className="text-white">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 tracking-tight">
                Panel de Administración
              </h1>
              <p className="text-blue-100 text-sm sm:text-base lg:text-lg">
                Gestión de usuarios y permisos del sistema
              </p>
            </div>
            
            <button 
              onClick={onBack}
              className="group flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-white py-3 px-6 rounded-xl hover:bg-white/20 transition-all duration-300 border border-white/20 hover:border-white/40 shadow-lg hover:shadow-xl"
            >
              <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="font-medium">Volver</span>
            </button>
          </div>
        </div>

        {/* Estadísticas de usuarios */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#0061A8] transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#63666A] font-medium">Total Usuarios</p>
                <p className="text-3xl font-bold text-[#0061A8] mt-2">{currentUsers.length}</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-[#0061A8] to-[#004d8a] rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#A8D08D] transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#63666A] font-medium">Administradores</p>
                <p className="text-3xl font-bold text-[#A8D08D] mt-2">
                  {currentUsers.filter(user => user.role === 'admin').length}
                </p>
              </div>
              <div className="p-3 bg-gradient-to-br from-[#A8D08D] to-[#8bc34a] rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-[#63666A] transform hover:-translate-y-1">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#63666A] font-medium">Usuarios Estándar</p>
                <p className="text-3xl font-bold text-[#63666A] mt-2">
                  {currentUsers.filter(user => user.role === 'standard').length}
                </p>
              </div>
              <div className="p-3 bg-gradient-to-br from-[#63666A] to-[#4a4d52] rounded-xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Sección para agregar nuevo usuario */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-3 bg-gradient-to-br from-[#A8D08D] to-[#8bc34a] rounded-xl">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-[#0061A8]">Agregar Nuevo Usuario</h3>
          </div>
          
          <form onSubmit={handleAddUser} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-[#63666A] mb-2">Email *</label>
                <input
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  className="w-full border-2 border-[#D1D3D4] rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#0061A8] focus:border-[#0061A8] transition-all duration-300"
                  placeholder="usuario@empresa.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#63666A] mb-2">Contraseña *</label>
                <input
                  type="password"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  className="w-full border-2 border-[#D1D3D4] rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#0061A8] focus:border-[#0061A8] transition-all duration-300"
                  placeholder="••••••••"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#63666A] mb-2">Nombre *</label>
                <input
                  type="text"
                  value={newUserFirstName}
                  onChange={(e) => setNewUserFirstName(e.target.value)}
                  className="w-full border-2 border-[#D1D3D4] rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#0061A8] focus:border-[#0061A8] transition-all duration-300"
                  placeholder="Juan"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#63666A] mb-2">Apellido *</label>
                <input
                  type="text"
                  value={newUserLastName}
                  onChange={(e) => setNewUserLastName(e.target.value)}
                  className="w-full border-2 border-[#D1D3D4] rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#0061A8] focus:border-[#0061A8] transition-all duration-300"
                  placeholder="Pérez"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-[#63666A] mb-2">Rol del Usuario</label>
                <select
                  value={newUserRole}
                  onChange={(e) => setNewUserRole(e.target.value)}
                  className="w-full border-2 border-[#D1D3D4] rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#0061A8] focus:border-[#0061A8] transition-all duration-300"
                >
                  <option value="standard">Usuario Estándar</option>
                  <option value="capacitador">Capacitador</option>
                  <option value="ejecutivo">Ejecutivo</option>
                  <option value="admin">Administrador</option>
                  <option value="tareas">Tareas</option>
                </select>
                {newUserRole && (
                  <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-medium text-blue-800">
                      {getRoleName(newUserRole)}: {getRoleDescription(newUserRole)}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#63666A] mb-2">Especialidad de Plataforma</label>
                <select
                  value={newUserPlatformSpecialty}
                  onChange={(e) => setNewUserPlatformSpecialty(e.target.value)}
                  className="w-full border-2 border-[#D1D3D4] rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#0061A8] focus:border-[#0061A8] transition-all duration-300"
                >
                  <option value="google_ads">🔵 Google Ads</option>
                  <option value="meta">🟢 Meta</option>
                  <option value="both">🟣 Ambas Plataformas</option>
                </select>
                <div className="mt-2 p-3 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border border-blue-200">
                  <p className="text-sm font-medium text-blue-800">
                    {newUserPlatformSpecialty === 'google_ads' && '🔵 Solo verá secciones de Google Ads'}
                    {newUserPlatformSpecialty === 'meta' && '🟢 Solo verá secciones de Meta'}
                    {newUserPlatformSpecialty === 'both' && '🟣 Verá todas las secciones (Google Ads + Meta)'}
                  </p>
                </div>
              </div>
            </div>

            {newUserRole !== 'admin' && (
              <div>
                <label className="block text-sm font-semibold text-[#63666A] mb-3">Células Asignadas</label>
                <div className="border-2 border-[#D1D3D4] rounded-xl p-4 bg-gradient-to-br from-gray-50 to-blue-50 max-h-60 overflow-y-auto">
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                    {allCells.map(cell => (
                      <label key={cell} className="group flex items-center space-x-2 cursor-pointer hover:bg-white p-3 rounded-lg transition-all duration-300 border border-transparent hover:border-[#0061A8] hover:shadow-md">
                        <input
                          type="checkbox"
                          value={cell}
                          checked={newUserCells.includes(cell)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewUserCells([...newUserCells, cell]);
                            } else {
                              setNewUserCells(newUserCells.filter(c => c !== cell));
                            }
                          }}
                          className="h-4 w-4 text-[#0061A8] focus:ring-[#0061A8] border-gray-300 rounded transition-all duration-300"
                        />
                        <span className="text-sm font-medium text-[#63666A] group-hover:text-[#0061A8] transition-colors">{cell}</span>
                      </label>
                    ))}
                  </div>
                </div>
                {newUserCells.length > 0 && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm font-medium text-blue-800">
                      Células seleccionadas ({newUserCells.length}): {newUserCells.join(', ')}
                    </p>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-start space-x-3">
                  <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-700 text-sm font-medium">{error}</p>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                className="group flex items-center space-x-2 bg-gradient-to-r from-[#0061A8] to-[#004d8a] text-white py-3 px-8 rounded-xl hover:from-[#004d8a] hover:to-[#0061A8] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
              >
                <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>Agregar Usuario</span>
              </button>
            </div>
          </form>
        </div>

        {/* Sección para listar usuarios existentes */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="p-6 sm:p-8 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gradient-to-br from-[#63666A] to-[#4a4d52] rounded-xl">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#0061A8]">Usuarios Existentes</h3>
            </div>
          </div>

          {/* Vista de tabla para desktop */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-blue-50">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-[#63666A] uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-[#63666A] uppercase tracking-wider">
                    Nombre Completo
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-[#63666A] uppercase tracking-wider">
                    Rol
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-[#63666A] uppercase tracking-wider">
                    Plataformas
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-bold text-[#63666A] uppercase tracking-wider">
                    Células Asignadas
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-bold text-[#63666A] uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentUsers.map((user, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#0061A8] to-[#004d8a] flex items-center justify-center">
                            <span className="text-white font-bold text-sm">
                              {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-[#0061A8]">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.firstName} {user.lastName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                        user.role === 'admin' 
                          ? 'bg-green-100 text-green-800' 
                          : user.role === 'capacitador'
                          ? 'bg-purple-100 text-purple-800'
                          : user.role === 'ejecutivo'
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {getRoleName(user.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {(() => {
                          const specialty = user.platform_specialty || 'both';
                          if (specialty === 'google_ads') {
                            return (
                              <span className="inline-flex items-center px-3 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">
                                🔵 Google Ads
                              </span>
                            );
                          } else if (specialty === 'meta') {
                            return (
                              <span className="inline-flex items-center px-3 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full">
                                🟢 Meta
                              </span>
                            );
                          } else {
                            return (
                              <div className="flex flex-wrap gap-1">
                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                                  🔵 Google
                                </span>
                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                                  🟢 Meta
                                </span>
                              </div>
                            );
                          }
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-[#63666A] max-w-xs">
                        {user.role === 'admin' ? (
                          <span className="font-medium text-green-600">Todas las células</span>
                        ) : (
                          user.cells && user.cells.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {user.cells.slice(0, 3).map(cell => (
                                <span key={cell} className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                                  {cell}
                                </span>
                              ))}
                              {user.cells.length > 3 && (
                                <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                                  +{user.cells.length - 3} más
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">Sin células asignadas</span>
                          )
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEditClick(user)}
                          className="group flex items-center space-x-1 text-[#0061A8] hover:text-[#A8D08D] transition-colors duration-200"
                        >
                          <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span>Editar</span>
                        </button>
                        <button
                          onClick={() => {
                            setPasswordUser(user);
                            setShowPasswordModal(true);
                            setNewPassword('');
                            setConfirmPassword('');
                            setPasswordError('');
                          }}
                          className="group flex items-center space-x-1 text-orange-600 hover:text-orange-800 transition-colors duration-200"
                        >
                          <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                          <span>Contraseña</span>
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.email)}
                          className="group flex items-center space-x-1 text-red-600 hover:text-red-800 transition-colors duration-200"
                        >
                          <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span>Eliminar</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Vista de cards para móvil y tablet */}
          <div className="lg:hidden p-4 space-y-4">
            {currentUsers.map((user, index) => (
              <div key={index} className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 border border-gray-200 hover:shadow-md transition-all duration-300">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#0061A8] to-[#004d8a] flex items-center justify-center">
                      <span className="text-white font-bold">
                        {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{user.firstName} {user.lastName}</h4>
                      <p className="text-sm text-[#0061A8]">{user.email}</p>
                    </div>
                  </div>
                  <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                    user.role === 'admin' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {user.role === 'admin' ? 'Admin' : 'Estándar'}
                  </span>
                </div>
                
                <div className="mb-3">
                  <p className="text-sm text-[#63666A] font-medium mb-2">Plataformas:</p>
                  <div className="flex items-center space-x-2">
                    {(() => {
                      const specialty = user.platform_specialty || 'both';
                      if (specialty === 'google_ads') {
                        return (
                          <span className="inline-flex items-center px-3 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">
                            🔵 Google Ads
                          </span>
                        );
                      } else if (specialty === 'meta') {
                        return (
                          <span className="inline-flex items-center px-3 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full">
                            🟢 Meta
                          </span>
                        );
                      } else {
                        return (
                          <div className="flex flex-wrap gap-1">
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                              🔵 Google
                            </span>
                            <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                              🟢 Meta
                            </span>
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-[#63666A] font-medium mb-2">Células asignadas:</p>
                  {user.role === 'admin' ? (
                    <span className="inline-flex px-3 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                      Todas las células
                    </span>
                  ) : (
                    user.cells && user.cells.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {user.cells.map(cell => (
                          <span key={cell} className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                            {cell}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 italic">Sin células asignadas</span>
                    )
                  )}
                </div>
                
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => handleEditClick(user)}
                    className="group flex items-center space-x-1 text-[#0061A8] hover:text-[#A8D08D] transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-white"
                  >
                    <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span className="text-sm font-medium">Editar</span>
                  </button>
                  <button
                    onClick={() => {
                      setPasswordUser(user);
                      setShowPasswordModal(true);
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordError('');
                    }}
                    className="group flex items-center space-x-1 text-orange-600 hover:text-orange-800 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-white"
                  >
                    <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    <span className="text-sm font-medium">Contraseña</span>
                  </button>
                  <button
                    onClick={() => handleDeleteUser(user.email)}
                    className="group flex items-center space-x-1 text-red-600 hover:text-red-800 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-white"
                  >
                    <svg className="w-4 h-4 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span className="text-sm font-medium">Eliminar</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Modal de edición de usuario */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 sm:p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-3 bg-gradient-to-br from-[#0061A8] to-[#004d8a] rounded-xl">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#0061A8]">
                  Editar Usuario: {editingUser.email}
                </h3>
              </div>
              
              <form onSubmit={handleSaveEdit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#63666A] mb-2">Nombre *</label>
                    <input
                      type="text"
                      value={editFirstName}
                      onChange={(e) => setEditFirstName(e.target.value)}
                      className="w-full border-2 border-[#D1D3D4] rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#0061A8] focus:border-[#0061A8] transition-all duration-300"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#63666A] mb-2">Apellido *</label>
                    <input
                      type="text"
                      value={editLastName}
                      onChange={(e) => setEditLastName(e.target.value)}
                      className="w-full border-2 border-[#D1D3D4] rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#0061A8] focus:border-[#0061A8] transition-all duration-300"
                      required
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-[#63666A] mb-2">Rol del Usuario</label>
                    <select
                      value={editRole}
                      onChange={(e) => setEditRole(e.target.value)}
                      className="w-full border-2 border-[#D1D3D4] rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#0061A8] focus:border-[#0061A8] transition-all duration-300"
                    >
                      <option value="standard">Usuario Estándar</option>
                      <option value="capacitador">Capacitador</option>
                      <option value="ejecutivo">Ejecutivo</option>
                      <option value="admin">Administrador</option>
                      <option value="tareas">Tareas</option>
                    </select>
                    {editRole && (
                      <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm font-medium text-blue-800">
                          {getRoleName(editRole)}: {getRoleDescription(editRole)}
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-[#63666A] mb-2">Especialidad de Plataforma</label>
                    <select
                      value={editPlatformSpecialty}
                      onChange={(e) => setEditPlatformSpecialty(e.target.value)}
                      className="w-full border-2 border-[#D1D3D4] rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-[#0061A8] focus:border-[#0061A8] transition-all duration-300"
                    >
                      <option value="google_ads">🔵 Google Ads</option>
                      <option value="meta">🟢 Meta</option>
                      <option value="both">🟣 Ambas Plataformas</option>
                    </select>
                    <div className="mt-2 p-3 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border border-blue-200">
                      <p className="text-sm font-medium text-blue-800">
                        {editPlatformSpecialty === 'google_ads' && '🔵 Solo verá secciones de Google Ads'}
                        {editPlatformSpecialty === 'meta' && '🟢 Solo verá secciones de Meta'}
                        {editPlatformSpecialty === 'both' && '🟣 Verá todas las secciones (Google Ads + Meta)'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {editRole !== 'admin' && (
                  <div>
                    <label className="block text-sm font-semibold text-[#63666A] mb-3">Células Asignadas</label>
                    <div className="border-2 border-[#D1D3D4] rounded-xl p-4 bg-gradient-to-br from-gray-50 to-blue-50 max-h-60 overflow-y-auto">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {allCells.map(cell => (
                          <label key={cell} className="group flex items-center space-x-2 cursor-pointer hover:bg-white p-3 rounded-lg transition-all duration-300 border border-transparent hover:border-[#0061A8] hover:shadow-md">
                            <input
                              type="checkbox"
                              value={cell}
                              checked={editCells.includes(cell)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setEditCells([...editCells, cell]);
                                } else {
                                  setEditCells(editCells.filter(c => c !== cell));
                                }
                              }}
                              className="h-4 w-4 text-[#0061A8] focus:ring-[#0061A8] border-gray-300 rounded transition-all duration-300"
                            />
                            <span className="text-sm font-medium text-[#63666A] group-hover:text-[#0061A8] transition-colors">{cell}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    {editCells.length > 0 && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm font-medium text-blue-800">
                          Células seleccionadas ({editCells.length}): {editCells.join(', ')}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {editError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-start space-x-3">
                      <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-red-700 text-sm font-medium">{editError}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setEditingUser(null)}
                    className="px-6 py-3 border-2 border-[#D1D3D4] text-[#63666A] rounded-xl hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="group flex items-center space-x-2 bg-gradient-to-r from-[#0061A8] to-[#004d8a] text-white py-3 px-6 rounded-xl hover:from-[#004d8a] hover:to-[#0061A8] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
                  >
                    <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Guardar Cambios</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de cambio de contraseña */}
      {showPasswordModal && passwordUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6 sm:p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="p-3 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1721 9z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0061A8]">Cambiar Contraseña</h3>
                  <p className="text-sm text-gray-600">{passwordUser.firstName} {passwordUser.lastName}</p>
                  <p className="text-xs text-gray-500">{passwordUser.email}</p>
                </div>
              </div>
              
              <form onSubmit={handleChangePassword} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-[#63666A] mb-2">Nueva Contraseña *</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full border-2 border-[#D1D3D4] rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300"
                    placeholder="••••••••"
                    required
                    minLength="6"
                  />
                  <p className="text-xs text-gray-500 mt-1">Mínimo 6 caracteres</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#63666A] mb-2">Confirmar Nueva Contraseña *</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full border-2 border-[#D1D3D4] rounded-xl shadow-sm py-3 px-4 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-300"
                    placeholder="••••••••"
                    required
                    minLength="6"
                  />
                </div>
                
                {passwordError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-start space-x-3">
                      <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-red-700 text-sm font-medium">{passwordError}</p>
                    </div>
                  </div>
                )}
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordUser(null);
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordError('');
                    }}
                    className="px-6 py-3 border-2 border-[#D1D3D4] text-[#63666A] rounded-xl hover:bg-gray-50 transition-colors font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="group flex items-center space-x-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 px-6 rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-semibold"
                  >
                    <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1721 9z" />
                    </svg>
                    <span>Cambiar Contraseña</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
